import React from 'react';
import {
  Card,
  CardImg,
  CardText,
  CardLink,
  CardBody,
  Alert,
  Button,
  ButtonDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from 'reactstrap';
import FontAwesomeIcon from '../../../../../components/Icons/FontAwesomeIcon';
import CardLoadingPlaceholder from '../../../../../components/Card/CardLoadingPlaceholder';
import propTypes from 'prop-types';
import UploadCard from './UploadCard';
import styled from 'styled-components';
import { getCacheImage } from '../../../../../utils/imageHelper'

const CardFooter = styled.div`
  justify-content: center;
  display: flex;
  align-items: center;
`;

const DropdownContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: flex-end;
  justify-content: flex-end;
`;

const PreviewWrapper = styled.div`
  width: 100%;
  height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #f8f9fa;
`;

const PreviewImage = styled(CardImg)`
  width: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transition: transform .2s ease;
`;

const RotationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: .5rem;
`;

const getErrorMessage = error => error?.message || error?.error?.message || 'Image update failed. Please try again.';

class ImageCard extends React.Component{
  constructor(props){
    super(props);
    this.state = {
      id: props.id,
      isDeleting: false,
      isUploading: false,
      isEdit: false,
      dropdownOpen: false
    }
    this._onRemoveImage = this._onRemoveImage.bind(this);
    this._handleToggleMenu = this._handleToggleMenu.bind(this);
    this._onChangeImage = this._onChangeImage.bind(this);
  }

  componentDidUpdate(prevProps) {
    if(prevProps.isProcessed && prevProps.isProcessed !== this.props.isProcessed){
      this.setState({
        isUploading: false,
        isDeleting: false
      });
    }
    if(prevProps.error !== this.props.error && this.props.error){
      this.setState({
        isUploading: false,
        isDeleting: false
      });
    }
  }

  _onRemoveImage(evt){
    this.setState({
      isDeleting: true
    });
    this.props.onRemoveImage(evt);
  }

  _onChangeImage(evt){
    if (evt.target.files && evt.target.files[0]){
      const { files } = evt.target;
      evt.persist();
      let reader = new FileReader();
      reader.onload = (e) => {
        files[0].preview = e.target.result;
        this.setState({
          isUploading: true,
          isEdit: false
        });
        this.props.onChangeImage(files, evt);
      };
      reader.readAsDataURL(files[0]);
    }
  }

  _handleToggleMenu(){
    this.setState({
      dropdownOpen: !this.state.dropdownOpen
    })
  }

  render(){
    const pendingRotation = this.props.pendingRotation || 0;
    const hasPendingRotation = pendingRotation !== 0;
    const isBusy = this.state.isUploading || this.state.isDeleting || this.props.isPreparingRotation || this.props.isProcessed || this.props.isQueued;
    const isDeleteBusy = this.state.isDeleting || this.props.isDeleted;
    const isActionDisabled = isBusy;

    return(
      <Card body  style={{'padding': 0}}>
        <CardLoadingPlaceholder
          isVisible={isBusy}
          loadingColor={isDeleteBusy? 'red': 'green'}
          loadingText={this.props.isPreparingRotation ? "Preparing..." : "Submitting..."}
        />
        <UploadCard
          id={this.props.id}
          error={this.props.error}
          isOpen={this.state.isEdit}
          onCancel={()=>this.setState({isEdit: !this.state.isEdit})}
          onChangeImage={this._onChangeImage}
        />
        <PreviewWrapper>
          <PreviewImage
            src={getCacheImage(this.props.previewURL)}
            alt={this.props.alt}
            style={{ transform: `rotate(${pendingRotation}deg)` }}
          />
        </PreviewWrapper>
        <CardBody style={{paddingTop: 0}}>
          <CardText>
            <small className="text-muted">Last updated {this.props.updated_at}</small>
          </CardText>
          {
            hasPendingRotation && (
              <RotationActions>
                <Button
                  color="primary"
                  size="sm"
                  className="mr-2 mb-2"
                  disabled={isActionDisabled}
                  onClick={() => this.props.onSaveRotation(this.props.id)}
                >
                  <FontAwesomeIcon iconType="save"/> Save Rotation
                </Button>
                <Button
                  color="link"
                  size="sm"
                  className="text-danger mb-2"
                  disabled={isActionDisabled}
                  onClick={() => this.props.onCancelRotation(this.props.id)}
                >
                  <FontAwesomeIcon iconType="times"/> Cancel
                </Button>
              </RotationActions>
            )
          }
          {
            this.props.error && (
              <Alert color="danger" className="py-2 mb-2">
                {getErrorMessage(this.props.error)}
              </Alert>
            )
          }
          <CardFooter>
            <CardLink href={getCacheImage(this.props.previewURL)} className="text-default text-center" target="_blank" rel="noopener noreferrer"><i className="fa fa-external-link"></i> Open</CardLink>
            <DropdownContainer>
              <ButtonDropdown isOpen={this.state.dropdownOpen} toggle={this._handleToggleMenu}>
                <DropdownToggle aria-label="Image actions">
                  <FontAwesomeIcon iconType="bars"/>
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem disabled={isActionDisabled} onClick={() => this.props.onRotateImage(this.props.id, -90)}>
                    <FontAwesomeIcon iconType="undo"/> Rotate Left
                  </DropdownItem>
                  <DropdownItem disabled={isActionDisabled} onClick={() => this.props.onRotateImage(this.props.id, 90)}>
                    <FontAwesomeIcon iconType="repeat"/> Rotate Right
                  </DropdownItem>
                  <DropdownItem disabled={isActionDisabled} onClick={()=> this.setState({isEdit: !this.state.isEdit})}>
                    <FontAwesomeIcon iconType="pencil"/> Change Image
                  </DropdownItem>
                  <DropdownItem disabled={isActionDisabled} onClick={this._onRemoveImage}>
                    <FontAwesomeIcon iconType="trash"/> Delete
                  </DropdownItem>
                </DropdownMenu>
              </ButtonDropdown>
            </DropdownContainer>
          </CardFooter>
        </CardBody>
      </Card>
    )
  }
}

export default ImageCard;
ImageCard.defaultProps = {
  error: null,
  isProcessed: false,
  isPreparingRotation: false,
  isQueued: false,
  isDeleted: false,
  pendingRotation: 0,
  statusCode: null,
  onRotateImage: () => {},
  onCancelRotation: () => {},
  onSaveRotation: () => {}
}
ImageCard.propTypes = {
  id: propTypes.oneOfType([propTypes.number, propTypes.string]),
  URL: propTypes.string,
  previewURL: propTypes.string,
  updated_at:propTypes.string,
  alt:propTypes.string,
  isProcessed: propTypes.bool,
  isPreparingRotation: propTypes.bool,
  isQueued: propTypes.bool,
  isDeleted: propTypes.bool,
  pendingRotation: propTypes.number,
  statusCode: propTypes.oneOfType([propTypes.number, propTypes.string]),
  error: propTypes.object,
  onRemoveImage: propTypes.func,
  onChangeImage: propTypes.func,
  onRotateImage: propTypes.func,
  onCancelRotation: propTypes.func,
  onSaveRotation: propTypes.func
}
